import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Bell } from "lucide-react";
import api from "@/lib/api";
import WaitlistModal from "@/components/WaitlistModal";
import CityRequestModal from "@/components/CityRequestModal";

const STATUS_LABELS = {
  now_booking: "Now Booking", coming_soon: "Coming Soon",
  heating_up: "Heating Up", requested: "Requested", closed: "Closed",
};

export default function Cities() {
  const [cities, setCities] = useState([]);
  const [waitlistCity, setWaitlistCity] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);

  useEffect(() => { api.get("/cities").then(r => setCities(r.data)).catch(() => setCities([])); }, []);

  const nowBooking = cities.filter(c => c.status === "now_booking");
  const comingNext = cities.filter(c => c.status === "coming_soon");
  const heating = cities.filter(c => c.status === "heating_up" || c.status === "requested");

  return (
    <div data-testid="cities-page" className="font-body">
      <section className="pt-28 pb-10 sm:pt-32 sm:pb-14">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">On the road</span>
          <h1 className="font-display font-semibold mt-3"
              style={{ fontSize: "clamp(1.875rem, 4vw, 3rem)", letterSpacing: "-0.02em", lineHeight: "1.1" }}>
            Your city. <span className="text-[#2d7cf6]">Private sessions.</span>
          </h1>
          <p className="mt-4 text-base text-[color:var(--rc-text)] max-w-[52ch]" style={{ lineHeight: "1.65" }}>
            Rebel&amp;Co moves from city to city — creating limited private opportunities to meet in person.
          </p>
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {nowBooking.length > 0 && (
        <section className="py-16 sm:py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <span className="rc-eyebrow">Now Booking</span>
            <div className="mt-8 space-y-4">
              {nowBooking.map((c) => (
                <div key={c.city_id} data-testid={`city-${c.city_id}`} className="rc-service-card flex flex-wrap items-center justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-2 text-[color:var(--rc-text-muted)]">
                      <MapPin className="w-4 h-4 text-[#2d7cf6]" />
                      <span className="text-xs uppercase tracking-[0.1em] font-semibold text-[#2d7cf6]">{STATUS_LABELS[c.status]}</span>
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold mt-3">{c.name}</h3>
                    <div className="mt-1 text-sm text-[color:var(--rc-text-muted)]">{c.dates}</div>
                    {c.timezone && <div className="mt-1 text-xs text-[color:var(--rc-text-dim)] uppercase tracking-[0.08em]">Timezone · {c.timezone.replace("_", " ")}</div>}
                  </div>
                  <Link to={`/dashboard/book?city=${c.city_id}`} state={{ city: c.city_id }} data-testid={`city-book-${c.city_id}`} className="rc-btn rc-btn-blue">
                    Book {c.name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {nowBooking.length === 0 && (
        <section className="py-16 sm:py-20" data-testid="cities-empty-state">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <span className="rc-eyebrow">Now Booking</span>
            <p className="font-display text-xl sm:text-2xl font-semibold mt-4 text-[color:var(--rc-text-muted)]">No cities currently open for booking.</p>
            <p className="mt-3 text-sm text-[color:var(--rc-text-muted)] max-w-xl leading-relaxed">Upcoming dates are listed below. Request availability to be first to know when hours open.</p>
          </div>
        </section>
      )}

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      {comingNext.length > 0 && (
        <section className="py-14 sm:py-16">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <span className="rc-eyebrow">Coming Next</span>
            <p className="mt-3 text-base text-[color:var(--rc-text)] max-w-[60ch]" style={{ lineHeight: "1.65" }}>
              Dates listed. Exact session hours and inclusions are being finalized — request availability to be first to know when booking opens.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {comingNext.map((c) => (
                <div key={c.city_id} data-testid={`city-${c.city_id}`} className="rc-service-card">
                  <div className="flex items-center gap-2 text-[color:var(--rc-text-muted)]">
                    <MapPin className="w-4 h-4 text-[#2d7cf6]" />
                    <span className="text-xs uppercase tracking-[0.1em] font-semibold">{STATUS_LABELS[c.status]}</span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-semibold mt-4">{c.name}</h3>
                  <div data-testid={`city-dates-${c.city_id}`} className="mt-2 text-base font-semibold text-[color:var(--rc-text)]">{c.dates}</div>
                  {c.timezone && <div className="mt-1 text-xs text-[color:var(--rc-text-muted)] uppercase tracking-[0.08em]">Timezone · {c.timezone.replace("_", " ")}</div>}
                  <button data-testid={`city-join-waitlist-${c.city_id}`} onClick={() => setWaitlistCity(c)} className="mt-6 rc-btn rc-btn-blue self-start">
                    <Bell className="w-4 h-4" /> {c.request_only ? "Request Availability" : "Join Waitlist"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {heating.length > 0 && (
        <>
          <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />
          <section className="py-16 sm:py-20">
            <div className="max-w-[1200px] mx-auto px-6 md:px-10">
              <span className="rc-eyebrow">Heating Up</span>
              <p className="mt-3 text-sm text-[color:var(--rc-text-muted)] max-w-xl">Where interest is building. Request your city to keep the momentum.</p>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {heating.map((c) => (
                  <div key={c.city_id} data-testid={`city-${c.city_id}`} className="rc-service-card">
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2d7cf6]" /><span className="text-xs uppercase tracking-[0.1em] font-semibold text-[color:var(--rc-text-muted)]">{STATUS_LABELS[c.status]}</span></div>
                    <h3 className="font-display text-xl font-semibold mt-4">{c.name}</h3>
                    {c.interest_count > 0 && <div className="mt-1 text-xs text-[color:var(--rc-text-muted)] uppercase tracking-[0.08em]">{c.interest_count} requesting</div>}
                    <button data-testid={`city-join-waitlist-${c.city_id}`} onClick={() => setWaitlistCity(c)} className="mt-5 rc-btn rc-btn-outline-new">Notify Me</button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      <section className="py-14 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">Request Your City</span>
          <h2 className="font-display font-semibold mt-3"
              style={{ fontSize: "clamp(1.625rem, 3.2vw, 2.25rem)", letterSpacing: "-0.015em", lineHeight: "1.15" }}>
            Where should Rebel go next?
          </h2>
          <p className="mt-4 text-base text-[color:var(--rc-text)] max-w-[56ch]" style={{ lineHeight: "1.65" }}>
            You request it. I&apos;ll watch where interest is building — cities move from requested to now booking based on real demand.
          </p>
          <button data-testid="request-city-cta" onClick={() => setRequestOpen(true)} className="mt-7 rc-btn rc-btn-blue">
            Request Your City <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {waitlistCity && <WaitlistModal city={waitlistCity} onClose={() => setWaitlistCity(null)} />}
      {requestOpen && <CityRequestModal onClose={() => setRequestOpen(false)} />}
    </div>
  );
}
